import { useEffect, useState } from "react";
import { Plus, Search, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import JaceLayout from "./JaceLayout";
import { JACE, TYPE_LABELS } from "./shared";

const EMPTY = { name: "", category: "", subcategory: "", skill: "", level: "BEGINNER", description: "", duration: 0, intensity: "", equipment: "", participants: "", contact_level: "", coaching_cues: [], common_mistakes: [], regression: "", progression: "", tags: [], notes: "", types: [] };

export default function JaceLibrary() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [type, setType] = useState("");
  const [edit, setEdit] = useState(null); // exercise object of null

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (category) params.category = category;
    if (type) params.type = type;
    api.get("/jace/exercises", { params }).then(({ data }) => setData(data)).catch(() => setData({ items: [], categories: [] }));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, category, type]); // eslint-disable-line

  const del = async (ex) => {
    if (!window.confirm(`"${ex.name}" verwijderen uit de library?`)) return;
    try {
      await api.delete(`/jace/exercises/${ex.id}`);
      toast.success("Oefening verwijderd");
      load();
    } catch { toast.error("Verwijderen mislukt"); }
  };

  return (
    <JaceLayout>
      <div data-testid="jace-library">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="section-tag section-tag-dark mb-3">// EXERCISE LIBRARY</div>
            <h2 className="font-display uppercase text-4xl md:text-6xl">Library.</h2>
          </div>
          <button onClick={() => setEdit({ ...EMPTY })} className="px-6 py-3.5 chip inline-flex items-center gap-2" style={{ background: JACE, color: "#0A0A0A" }} data-testid="jace-add-exercise-btn">
            <Plus size={14}/> NEW EXERCISE
          </button>
        </div>

        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative">
            <Search size={13} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#4A4A4A]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek oefening…" className="editorial-input dark !w-56 !pl-5" data-testid="jace-lib-search" />
          </div>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="editorial-input dark !w-auto" data-testid="jace-lib-category">
            <option value="" className="bg-[#0A0A0A]">Alle categorieën</option>
            {(data?.categories || []).map((c) => <option key={c} value={c} className="bg-[#0A0A0A]">{c}</option>)}
          </select>
          <select value={type} onChange={(e) => setType(e.target.value)} className="editorial-input dark !w-auto" data-testid="jace-lib-type">
            <option value="" className="bg-[#0A0A0A]">Alle training types</option>
            {Object.entries(TYPE_LABELS).map(([k, l]) => <option key={k} value={k} className="bg-[#0A0A0A]">{l}</option>)}
          </select>
          <span className="chip text-[#4A4A4A] self-center" data-testid="jace-lib-count">{data ? `${data.count} oefeningen` : ""}</span>
        </div>

        {data === null ? (
          <div className="chip text-[#A3A3A3]">Laden…</div>
        ) : data.items.length === 0 ? (
          <div className="border border-[#F5F3F0]/15 p-10 text-center chip text-[#4A4A4A]" data-testid="jace-lib-empty">Geen oefeningen gevonden.</div>
        ) : (
          <div className="line-t line-dark" data-testid="jace-lib-list">
            {data.items.map((ex) => (
              <div key={ex.id} className="line-b line-dark py-3.5 flex items-center gap-4 group" data-testid={`jace-ex-${ex.id}`}>
                <button onClick={() => setEdit(ex)} className="flex-1 text-left flex items-center gap-4 min-w-0">
                  <span className="font-display text-lg uppercase truncate group-hover:text-[#C6FF3C] transition-colors">{ex.name}</span>
                </button>
                <span className="chip text-[#A3A3A3] w-40 truncate hidden md:inline">{ex.category}</span>
                <span className="chip text-[#A3A3A3] w-24 hidden lg:inline">{ex.level}</span>
                <span className="chip text-[#4A4A4A] w-16 text-right hidden sm:inline">{ex.duration || 0} MIN</span>
                {ex.migrated_from === "JOTS" && <span className="chip text-[#4A4A4A] hidden xl:inline">JOTS</span>}
                <button onClick={() => setEdit(ex)} className="w-8 h-8 border border-[#F5F3F0]/20 flex items-center justify-center text-[#A3A3A3] hover:text-[#F5F3F0]" data-testid={`jace-ex-edit-${ex.id}`}><Pencil size={12}/></button>
                <button onClick={() => del(ex)} className="w-8 h-8 border border-[#F5F3F0]/20 flex items-center justify-center text-[#A3A3A3] hover:text-red-400" data-testid={`jace-ex-del-${ex.id}`}><Trash2 size={12}/></button>
              </div>
            ))}
          </div>
        )}

        {edit && <ExerciseModal ex={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      </div>
    </JaceLayout>
  );
}

function ExerciseModal({ ex, onClose, onSaved }) {
  const isNew = !ex.id;
  const [f, setF] = useState({ ...EMPTY, ...ex, coaching_cues: (ex.coaching_cues || []).join("\n"), tags: (ex.tags || []).join(", ") });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...f,
      duration: parseInt(f.duration) || 0,
      coaching_cues: f.coaching_cues.split("\n").map((s) => s.trim()).filter(Boolean),
      tags: f.tags.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (isNew) await api.post("/jace/exercises", payload);
      else await api.put(`/jace/exercises/${ex.id}`, payload);
      toast.success(isNew ? "Oefening toegevoegd" : "Oefening bijgewerkt");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Opslaan mislukt"); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="jace-exercise-modal">
        <div className="chip text-[#A3A3A3]">// EXERCISE LIBRARY</div>
        <div className="font-display uppercase text-3xl mt-1 mb-6">{isNew ? "New exercise." : "Edit exercise."}</div>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><label className="chip text-[#A3A3A3]">Naam*</label><input required value={f.name} onChange={(e) => set("name", e.target.value)} className="editorial-input dark" data-testid="jace-ex-name" /></div>
          <div><label className="chip text-[#A3A3A3]">Categorie</label><input value={f.category} onChange={(e) => set("category", e.target.value)} className="editorial-input dark" data-testid="jace-ex-category" /></div>
          <div><label className="chip text-[#A3A3A3]">Subcategorie</label><input value={f.subcategory} onChange={(e) => set("subcategory", e.target.value)} className="editorial-input dark" /></div>
          <div><label className="chip text-[#A3A3A3]">Level</label>
            <select value={f.level} onChange={(e) => set("level", e.target.value)} className="editorial-input dark" data-testid="jace-ex-level">
              {["", "BEGINNER", "INTERMEDIATE", "ADVANCED", "MIXED"].map((l) => <option key={l} value={l} className="bg-[#0A0A0A]">{l || "—"}</option>)}
            </select>
          </div>
          <div><label className="chip text-[#A3A3A3]">Duur (min)</label><input type="number" min="0" value={f.duration} onChange={(e) => set("duration", e.target.value)} className="editorial-input dark" data-testid="jace-ex-duration" /></div>
          <div className="col-span-2"><label className="chip text-[#A3A3A3]">Beschrijving</label><textarea rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} className="editorial-input dark w-full" data-testid="jace-ex-description" /></div>
          <div><label className="chip text-[#A3A3A3]">Equipment</label><input value={f.equipment} onChange={(e) => set("equipment", e.target.value)} className="editorial-input dark" /></div>
          <div><label className="chip text-[#A3A3A3]">Intensiteit</label><input value={f.intensity} onChange={(e) => set("intensity", e.target.value)} className="editorial-input dark" /></div>
          <div className="col-span-2"><label className="chip text-[#A3A3A3]">Coaching cues (één per regel)</label><textarea rows={2} value={f.coaching_cues} onChange={(e) => set("coaching_cues", e.target.value)} className="editorial-input dark w-full" /></div>
          <div><label className="chip text-[#A3A3A3]">Regression (makkelijker)</label><input value={f.regression} onChange={(e) => set("regression", e.target.value)} className="editorial-input dark" data-testid="jace-ex-regression" /></div>
          <div><label className="chip text-[#A3A3A3]">Progression (zwaarder)</label><input value={f.progression} onChange={(e) => set("progression", e.target.value)} className="editorial-input dark" data-testid="jace-ex-progression" /></div>
          <div className="col-span-2">
            <label className="chip text-[#A3A3A3]">Beschikbaar voor</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {Object.entries(TYPE_LABELS).map(([k, l]) => {
                const on = (f.types || []).includes(k);
                return (
                  <button type="button" key={k} onClick={() => set("types", on ? f.types.filter((t) => t !== k) : [...(f.types || []), k])}
                    className="chip px-3 py-1.5 border transition-colors"
                    style={on ? { borderColor: JACE, color: JACE } : { borderColor: "#F5F3F033", color: "#A3A3A3" }}
                    data-testid={`jace-ex-type-${k}`}>{l}</button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button type="submit" disabled={busy} className="flex-1 py-3 chip" style={{ background: JACE, color: "#0A0A0A" }} data-testid="jace-ex-save">{busy ? "Opslaan…" : "Opslaan"}</button>
          <button type="button" onClick={onClose} className="btn-ghost-dark" data-testid="jace-ex-cancel">Annuleren</button>
        </div>
      </form>
    </div>
  );
}
